// IEA Site Survey Collect (pass 2) on the Map screen. Words: IEA_STEP4_COLLECT_WORDS.md.
// The Map page owns the screen; this module owns Collect's state, taps and saves.
// Saves: one IEA Data record per asset (ID = Asset ID), and the wire fields on the Map's Connections row.
// Everything goes through the Outbox: on the iPad first, then the cloud.

import { deviceList, photoPath } from "./outbox";
import { saveRecord, savePhoto } from "./survey-sync";
import type { SitePack } from "./survey";
import type { MapLink, MapTree, Registry, Stamp } from "./survey-map";
import { rowPhotoKeys, opensRow,
  cardFor,
  deletedEntry,
  deletedKey,
  emptyWire,
  guidedTypes,
  labelChoices,
  relabelChange,
  fieldChange,
  finished,
  ieaPath,
  isMccAsset,
  leftBelow,
  lookedAt,
  mergeData,
  nodeStatus,
  notVisibleChange,
  placeChange,
  prefills,
  quickPicks,
  recFromData,
  recOf,
  rowFields,
  rowRec,
  rowsLeft,
  rowsOf,
  circuitsOf,
  spacesOf,
  totals,
  wireFromRow,
  wireLinkOf,
  wireOf,
  wireRequired,
  wireRow,
  wireStatus,
  wireTag,
  workList,
  type Field,
  type ListValue,
  type Rec,
  type Site,
  type Wire,
  sameAboveUndo,
} from "./survey-collect";
import { collectPanel, otherSheet, wireSheet, workListSheet } from "./survey-collect-screens";
import { openCamera } from "./survey-camera";
import { srcOf, thumb, uploadMark, type ViewItem } from "./survey-photo-store";
import type { CollectDecor } from "./survey-map-screens";
import { gearKindFor, gearOverlay, SIZE_PICK, type GearMode, type GearState } from "./survey-gear-screens";
import { MAIN_NAME, panelSlots, panelWalk, walkOrder } from "./survey-gear";

/** What the Map page lends Collect. */
export interface CollectCtx {
  company: string;
  pack: SitePack;
  reg: Registry;
  tree: () => MapTree;
  links: () => MapLink[];
  cur: () => string | null;
  select: (key: string | null) => void;
  paint: () => void;
  flash: (text: string) => void;
  /** The Map's 6-second Undo bar (CANCEL_WORDS.md section 3). */
  undo: (text: string, run: () => void) => void;
  stamp: () => Stamp;
  /** Writes onto a Connections row through the Map's own path (keeps the row whole on the iPad). */
  writeLink: (id: string, data: Record<string, unknown>, assetId: string, card: string) => Promise<void>;
  /** Model Tags at this site, for the card headers. */
  tags?: () => readonly import("./survey-tags").TagRecord[];
  /** The prefixes in use (Lists page, Model Tag Prefixes). */
  prefixes?: () => import("./survey-tags").Prefixes;
}

type Sheet = { kind: "wire"; link: string; removable?: boolean } | { kind: "work" } | { kind: "other"; target: string; field: string; title: string } | null;

interface State {
  pack: SitePack;
  /** Stored IEA Data by Asset ID: the pack, then this iPad's saves laid over it. */
  data: Record<string, Record<string, unknown>>;
  recs: Record<string, Rec>;
  wires: Record<string, Wire>;
  wireOrder: string[];
  row: string | null;
  tab: "device" | "wire";
  openCards: Set<string>;
  sheet: Sheet;
  /** The open door: an MCC's gear front (IEA_GEAR_VIEW_WORDS.md). */
  gear: GearState | null;
}

let s: State | null = null;
let ctx: CollectCtx | null = null;

function lists(): ListValue[] {
  return ctx?.pack.fieldLists ?? [];
}

/** Opens Collect state for the pack once; later calls keep it. */
export function collectInit(c: CollectCtx): void {
  ctx = c;
  if (s && s.pack === c.pack) return;
  const data: Record<string, Record<string, unknown>> = { ...(c.pack.ieaRecords ?? {}) };
  const wires: Record<string, Wire> = {};
  for (const l of c.pack.links) if (l.id && l.row) wires[l.id] = wireFromRow(l.row);
  s = { pack: c.pack, data, recs: {}, wires, wireOrder: Object.keys(wires), row: null, tab: "device", openCards: new Set(), sheet: null, gear: null };
  for (const [id, d] of Object.entries(data)) s.recs[id] = recFromData(d);
  void loadLocal();
}

/** This iPad's saves lie over the pack, so a refresh never hides them. */
async function loadLocal(): Promise<void> {
  if (!s || !ctx) return;
  const base = `companies/${ctx.company}/sites/${ctx.pack.siteNumber}/`;
  try {
    const recs = (await deviceList.records()).filter((r) => r.path.startsWith(base)).sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    for (const r of recs) {
      if (r.path.startsWith(`${base}ieaData/`)) {
        const id = r.path.slice(`${base}ieaData/`.length);
        s.data[id] = mergeData(s.data[id] ?? {}, r.data);
        s.recs[id] = recFromData(s.data[id]);
      } else if (r.path.startsWith(`${base}connections/`)) {
        const id = r.path.slice(`${base}connections/`.length);
        if ("Link Type" in r.data || "Conductor Size" in r.data) {
          s.wires[id] = wireFromRow({ ...wireRow(s.wires[id] ?? emptyWire()), ...r.data });
          s.wireOrder = [...s.wireOrder.filter((x) => x !== id), id];
        }
      }
    }
  } catch {
    // No device list. The pack stands.
  }
  ctx.paint();
}

/** Collect's view of the site, for Photos mode (survey-photos-page.ts). */
export function collectSite(): Site {
  return site();
}

function site(): Site {
  return { reg: ctx!.reg, tree: ctx!.tree(), links: ctx!.links(), recs: s!.recs, wires: s!.wires, ieeeClassOf: ctx!.pack.ieeeClassOf };
}

function prefillFor(assetId: string): Record<string, string | number> {
  const a = ctx!.reg.byId.get(assetId);
  if (!a) return {};
  return prefills(cardFor(site(), assetId), a, ctx!.pack.ieeeClassOf[a.equipmentCode] ?? "", ctx!.reg.isPosition(assetId));
}

/** The diagram's Collect layer and the left panel. */
export function collectDecor(): CollectDecor {
  const st = site();
  const t = st.tree;
  return {
    status: (key) => {
      const n = t.nodes.get(key);
      return n ? nodeStatus(st, n) : null;
    },
    tag: (key) => {
      const n = t.nodes.get(key);
      const lid = n ? wireLinkOf(n) : null;
      if (!n || !lid) return null;
      const w = wireOf(st, lid);
      return { text: wireTag(w), state: wireStatus(w), optional: !wireRequired(n) };
    },
    left: (key) => leftBelow(st, key),
    rowsLeft: (key) => {
      const n = t.nodes.get(key);
      return n && n.kind === "asset" && cardFor(st, n.ref).kind === "housing" ? rowsLeft(st, n.ref) : 0;
    },
    totals: totals(st),
    panel: collectPanel(st, panelState()),
    next: nextOpen(st),
  };
}

/** Next Not Done (Robert 2026-10-19): the next line on the work list, after the pick, that still has
 *  something left. Wraps to the top. A position or a load counts as its housing. */
function nextOpen(st: Site): { key: string; label: string } | null {
  const lines = workList(st).rooms.flatMap((r) => r.lines);
  const open = (l: (typeof lines)[number]) => !finished(l.status) || l.rowsLeft > 0 || l.wireLeft;
  let cur = ctx!.cur();
  let n = cur ? st.tree.nodes.get(cur) : undefined;
  while (n && (n.kind === "point" || n.kind === "load" || n.kind === "room") && n.parent) {
    cur = n.parent;
    n = st.tree.nodes.get(cur);
  }
  const at = cur ? lines.findIndex((l) => l.key === cur) : -1;
  for (let i = 1; i <= lines.length; i++) {
    const l = lines[(at + i + lines.length) % lines.length]!;
    if (l.key !== cur && open(l)) return { key: l.key, label: l.label };
  }
  return null;
}

function panelState() {
  return {
    siteNumber: ctx!.pack.siteNumber,
    cur: ctx!.cur(),
    row: s!.row,
    tab: s!.tab,
    openCards: s!.openCards,
    lists: lists(),
    prefill: prefillFor,
    pic: thumb,
    mark: uploadMark,
    tags: ctx!.tags?.() ?? [],
    prefixes: ctx!.prefixes?.(),
  };
}

/** What the room cards draw with (SURVEY_CARDS_C1_WORDS.md): Collect's site and panel state, the same saves. */
export function cardsParts(): { site: Site; panel: ReturnType<typeof panelState> } {
  return { site: site(), panel: panelState() };
}

/**
 * Fed From changed on a card (item 13): the old feed is removed, a new one made, so its wire follows it. Nothing
 * to carry when the old feed had no wire.
 */
export async function moveWire(fromLink: string, toLink: string): Promise<void> {
  if (!s) return;
  const w = s.wires[fromLink];
  if (!w || wireStatus(w) === "none") return;
  await saveWire(toLink, { ...w });
}

// ---- The gear front (Gear View, step 1: the MCC in Collect) ----

/** The open door, drawn over the screen. Empty when closed. */
export function gearSheet(mode: GearMode = "collect", photosOk = true): string {
  if (!s || !s.gear || !ctx) return "";
  const st = site();
  if (!st.reg.byId.has(s.gear.assetId)) {
    s.gear = null;
    return "";
  }
  return gearOverlay(st, panelState(), s.gear, { mode, photosOk, src: srcOf });
}

/** The housing whose door is open, or null. */
export function gearAsset(): string | null {
  return s?.gear?.assetId ?? null;
}

/** The bucket or breaker picked in the open door, or null. */
export function gearPick(): string | null {
  return s?.gear ? s.row : null;
}

/** A housing tapped in Map or Photos opens its door the same way (Gear View step 3). */
export function gearForKey(key: string): boolean {
  if (!s) return false;
  s.row = null;
  gearFor(key);
  const n = ctx!.tree().nodes.get(key);
  if (s.gear && n?.kind === "point") s.row = n.ref;
  return Boolean(s.gear);
}

export function gearOpen(): boolean {
  return Boolean(s?.gear);
}

/** An MCC, or a bucket in one, opens the door. Anything else closes it. */
function gearFor(key: string): void {
  if (!s) return;
  const n = ctx!.tree().nodes.get(key);
  const st = site();
  if (n?.kind === "asset" && gearKindFor(st, n.ref)) s.gear = { assetId: n.ref, view: "front" };
  else if (n?.kind === "point" && gearKindFor(st, n.asset)) s.gear = { assetId: n.asset, view: "front" };
  else s.gear = null;
}

export function openGear(assetId: string): void {
  if (!s || !assetId) return;
  s.gear = { assetId, view: "front" };
  ctx?.paint();
}

export function closeGear(): void {
  if (!s) return;
  s.gear = null;
  ctx?.paint();
}

export function setGearView(v: "front" | "table"): void {
  if (!s?.gear) return;
  s.gear.view = v;
  ctx?.paint();
}

// ---- Panel circles (Gear View words 9) ----

/** A refusal shows at once: flash only paints when it clears. */
function say(text: string): void {
  ctx!.flash(text);
  ctx!.paint();
}

/** Circuits a numbered breaker or space already takes: itself and the slots its poles span. */
function takenCircuits(assetId: string, except = ""): Map<number, string> {
  const st = site();
  const out = new Map<number, string>();
  for (const row of rowsOf(st, assetId)) {
    if (row.point === except || !/^\d+$/.test(row.point)) continue;
    const poles = Number(rowRec(st, assetId, row).f.Poles) || 1;
    for (let i = 0; i < poles; i++) out.set(Number(row.point) + 2 * i, row.point);
  }
  for (const k of spacesOf(st, assetId)) if (k !== except) out.set(Number(k), k);
  return out;
}

/** Panel size: the number of circuits it was built for. Never smaller than a circuit already used. */
export async function setCircuits(value: string): Promise<void> {
  if (!s?.gear) return;
  const n = Math.round(Number(value));
  if (!Number.isFinite(n) || n < 2 || n > 120) {
    say("Type the number of circuits, 2 to 120.");
    return;
  }
  const high = Math.max(0, ...takenCircuits(s.gear.assetId).keys());
  if (n < high) {
    say(`Circuit ${high} is already recorded. The panel cannot be smaller than ${high}.`);
    return;
  }
  if (s.row === SIZE_PICK) s.row = null;
  await setField(`A:${s.gear.assetId}`, "Circuits", String(n));
}

/** Tap an empty circle: a 1-pole breaker there, its card open. */
export async function addBreaker(point: string): Promise<void> {
  if (!s?.gear) return;
  s.row = point;
  await setField(`B:${s.gear.assetId}:${point}`, "Slot", "Breaker");
  document.getElementById("gv-side")?.scrollTo({ top: 0 });
}

/** Poles 1, 2 or 3. A 3-pole on 13 takes 15 and 17, so both must be open and inside the panel. */
export async function setPoles(poles: number): Promise<void> {
  if (!s?.gear || !s.row) return;
  const n = Number(s.row);
  const taken = takenCircuits(s.gear.assetId, s.row);
  const size = circuitsOf(site(), s.gear.assetId) ?? Infinity;
  for (let i = 1; i < poles; i++) {
    const c = n + 2 * i;
    if (c > size) return say(`Circuit ${c} is past the panel size (${size}).`);
    if (taken.has(c)) return say(`Circuit ${c} already holds a ${spacesOf(site(), s.gear.assetId).includes(String(c)) ? "space" : "breaker"}. Clear it first.`);
  }
  await setField(`B:${s.gear.assetId}:${s.row}`, "Poles", String(poles));
}

/** Space, Removed or Breaker for the circle that is open. A breaker the Map uses stays. */
export async function setSlot(slot: "Space" | "Removed" | "Breaker"): Promise<void> {
  if (!s?.gear || !s.row) return;
  const point = s.row;
  // Keep the panel: the door can close while the first save is still going.
  const id = s.gear.assetId;
  if (slot !== "Breaker" && ctx!.tree().nodes.get(`p:${id}:${point}`)) {
    say("This breaker is on the Map. Remove it on the Map.");
    return;
  }
  if (slot === "Removed") s.row = null;
  if (slot === "Space") await setField(`B:${id}:${point}`, "Poles", "1");
  await setField(`B:${id}:${point}`, "Slot", slot);
}

/** Tap a bucket: its card opens on the right. Tap it again to put the card away. */
export function pickBucket(point: string): void {
  if (!s?.gear) return;
  s.row = s.row === point ? null : point;
  ctx?.paint();
  document.getElementById("gv-side")?.scrollTo({ top: 0 });
}

export function collectSheet(): string {
  if (!s || !s.sheet) return "";
  const st = site();
  if (s.sheet.kind === "work") {
    const w = workList(st);
    return workListSheet(ctx!.pack.siteName, w.rooms, w.totals);
  }
  if (s.sheet.kind === "other") return otherSheet(s.sheet.title, s.sheet.field);
  const link = s.sheet.link;
  const node = [...st.tree.nodes.values()].find((n) => wireLinkOf(n) === link);
  return wireSheet(st, link, node ? wireRequired(node) : true, lists(), quickPicks(s.wireOrder.map((id) => s!.wires[id]!).filter(Boolean)), s.sheet.removable === true);
}

/** A node tapped on the diagram in Collect. A position opens its housing's table at that row. */
export function collectSelected(key: string): string {
  if (!s) return key;
  const n = ctx!.tree().nodes.get(key);
  s.row = null;
  gearFor(key);
  if (n?.kind === "point") {
    s.row = n.ref;
    s.tab = "device";
    return `a:${n.asset}`;
  }
  return key;
}

export function openWire(key: string): void {
  if (!s) return;
  const n = ctx!.tree().nodes.get(key);
  const lid = n ? wireLinkOf(n) : null;
  if (!lid) return;
  s.sheet = { kind: "wire", link: lid };
  ctx!.paint();
}

/** A side feed's wire (second source, a bucket fed separately) has no tag of its own on the tree. On the Drawing the
 *  card also carries Remove Line (SURVEY_DRAWING_GRID_C1_WORDS.md item 23). */
export function openWireLink(linkId: string, removable = false): void {
  if (!s || !linkId) return;
  s.sheet = { kind: "wire", link: linkId, removable };
  ctx!.paint();
}

export function openWorkList(): void {
  if (!s) return;
  s.sheet = { kind: "work" };
  ctx!.paint();
}

// ---- Saving ----

function parseTarget(t: string): { assetId: string; breaker?: string } {
  if (t.startsWith("B:")) {
    const rest = t.slice(2);
    const i = rest.lastIndexOf(":");
    return { assetId: rest.slice(0, i), breaker: rest.slice(i + 1) };
  }
  return { assetId: t.slice(2) };
}

function recAt(t: string): Rec {
  const p = parseTarget(t);
  const r = recOf(site(), p.assetId);
  return p.breaker ? (r.breakers[p.breaker] ?? recFromData(undefined)) : r;
}

/** One save to one IEA Data record: in memory now, then the device list, then the cloud. */
async function saveIea(target: string, change: Record<string, unknown>, card: string): Promise<string> {
  const c = ctx!;
  const p = parseTarget(target);
  const stamp = c.stamp();
  // First save on a record carries the Typical prefills, so Done can count them (1584.1 4.3).
  const first = !s!.data[p.assetId];
  const pre = first && !p.breaker ? prefillFor(p.assetId) : {};
  const preData: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(pre)) preData[k] = v;
  if (Object.keys(pre).length) preData["Field Sources"] = Object.fromEntries(Object.keys(pre).map((k) => [k, "Typical"]));
  const body = mergeData(preData, placeChange({ assetId: p.assetId, breaker: p.breaker, data: change }));
  const data = { ...body, "Asset ID": p.assetId, "Site Number": stamp.siteNumber, "Recorded By": stamp.badgeId, "Holder Name": stamp.holderName, "Recorded On": stamp.deviceName, "Recorded At": stamp.at };
  s!.data[p.assetId] = mergeData(s!.data[p.assetId] ?? {}, data);
  s!.recs[p.assetId] = recFromData(s!.data[p.assetId]);
  const path = ieaPath(c.company, stamp.siteNumber, p.assetId);
  let full: Record<string, unknown> = data;
  try {
    const prev = (await deviceList.records()).find((r) => r.path === path);
    if (prev) full = mergeData(prev.data, data);
  } catch {
    // No device list: send this save as is.
  }
  await saveRecord({ path, company: c.company, siteNumber: stamp.siteNumber, assetId: p.assetId, card, data: full, savedAt: stamp.at });
  return path;
}

function fieldOf(target: string, name: string): Field {
  const p = parseTarget(target);
  const card = cardFor(site(), p.assetId);
  const all = [...card.fields, ...rowFields(isMccAsset(site(), p.assetId) || isMccAsset(site(), housingOf(p.assetId)))];
  return all.find((f) => f.name === name) ?? { name };
}

function housingOf(assetId: string): string {
  for (const h of ctx!.reg.assets) if (assetId.startsWith(`${h.assetId}-`) && ctx!.reg.positionsOf(h.assetId).includes(assetId.slice(h.assetId.length + 1))) return h.assetId;
  return assetId;
}

/** Bucket and breaker values set Opened when it is blank (opensRow). */
function withOpened(target: string, change: Record<string, unknown>, value: string): Record<string, unknown> {
  const p = parseTarget(target);
  const isRow = Boolean(p.breaker) || ctx!.reg.isPosition(p.assetId);
  return opensRow(recAt(target), isRow, value) ? { ...change, Opened: "Opened" } : change;
}

function cardWord(target: string): string {
  const p = parseTarget(target);
  if (p.breaker) return `Breaker ${p.breaker}`;
  return ctx!.reg.isPosition(p.assetId) ? "Bucket or breaker" : `${cardFor(site(), p.assetId).title} card`;
}

export async function setField(target: string, name: string, value: string): Promise<void> {
  if (!s) return;
  if (value === "Other") {
    s.sheet = { kind: "other", target, field: name, title: `Other ${name}` };
    ctx!.paint();
    return;
  }
  const f = fieldOf(target, name);
  const pre = prefillFor(parseTarget(target).assetId)[name];
  ctx!.paint();
  await saveIea(target, withOpened(target, fieldChange(f, value, pre), value), cardWord(target));
  ctx!.paint();
}

/** A required field tapped into and left blank is looked at (SURVEY_DONE_LOOKED_C1_WORDS.md item 1): its amber clears
 *  and the light can go blue. A value already there, or a field not required, needs nothing. `paint` is false when the
 *  tap went on to another field, so the redraw never takes that field out from under the finger; the next save repaints. */
export async function markLooked(target: string, name: string, paint: boolean): Promise<void> {
  if (!s) return;
  const f = fieldOf(target, name);
  const r = recAt(target);
  if (!f.req || f.office || lookedAt(r, f)) return;
  await saveIea(target, { "Looked At": { [name]: true } }, cardWord(target));
  if (paint) ctx!.paint();
}

export async function toggleNotVisible(target: string, name: string): Promise<void> {
  const on = recAt(target).src[name] !== "Not Visible";
  await saveIea(target, notVisibleChange(fieldOf(target, name), on), cardWord(target));
  ctx!.paint();
}

export async function setOpened(target: string, value: string): Promise<void> {
  const change: Record<string, unknown> = { Opened: value };
  if (value !== "Not Opened") change["Not Opened Reason"] = "";
  await saveIea(target, change, cardWord(target));
  ctx!.paint();
}

export async function setReason(target: string, value: string): Promise<void> {
  if (value === "Other") {
    s!.sheet = { kind: "other", target, field: "Not Opened Reason", title: "Other Reason" };
    ctx!.paint();
    return;
  }
  await saveIea(target, { "Not Opened Reason": value }, cardWord(target));
  ctx!.paint();
}

/** The Other sheet's typed word. */
export async function submitOther(value: string): Promise<void> {
  if (!s || s.sheet?.kind !== "other") return;
  const { target, field } = s.sheet;
  s.sheet = null;
  const v = value.trim();
  if (!v) return ctx!.paint();
  if (field === "Not Opened Reason") await saveIea(target, { "Not Opened Reason": `Other: ${v}` }, cardWord(target));
  else await saveIea(target, withOpened(target, fieldChange(fieldOf(target, field), v, undefined), v), cardWord(target));
  ctx!.paint();
}

export async function setLoadHp(linkId: string, value: string): Promise<void> {
  const l = ctx!.links().find((x) => x.id === linkId);
  if (!l) return;
  const st = ctx!.stamp();
  await ctx!.writeLink(linkId, { "Connection ID": linkId, "Site Number": st.siteNumber, "Recorded By": st.badgeId, "Holder Name": st.holderName, "Recorded On": st.deviceName, "Recorded At": st.at, "End Load HP": value.trim() }, l.fromAsset, "Load HP");
  ctx!.paint();
}

// ---- Photos (IEA_PHOTO_FLOW_WORDS.md, Robert 2026-10-16) ----

const today = () => new Date().toISOString().slice(0, 10);

/** "MSD-01", or "MCC-1A · B4" for a row. Shown on the camera and the big view. */
function photoTitle(target: string): string {
  const p = parseTarget(target);
  const short = (id: string) => id.replace(/^\d{2}-/, "");
  if (p.breaker) return `${short(p.assetId)} · ${p.breaker}`;
  if (ctx!.reg.isPosition(p.assetId)) {
    const h = housingOf(p.assetId);
    return `${short(h)} · ${p.assetId.slice(h.length + 1)}`;
  }
  return short(p.assetId);
}

/** A row photo lives on a position asset (A:) or a typed breaker (B:). Rows have up to 4: "Row" (photo 1), then Photo 2 to 4. */
function isRowTarget(target: string): boolean {
  const p = parseTarget(target);
  return Boolean(p.breaker) || ctx!.reg.isPosition(p.assetId);
}

/** Old copy of a photo being replaced or deleted: off the iPad if it never uploaded, else a Deleted Photos line. */
async function retire(target: string, type: string, path: string, why: "Deleted" | "Retaken"): Promise<Record<string, unknown>> {
  try {
    const local = await deviceList.photo(path);
    if (local && local.state !== "sent") {
      await deviceList.deletePhoto(path);
      return {};
    }
  } catch {
    // Device list unreadable: keep the record line so nothing is lost.
  }
  const st = ctx!.stamp();
  return { "Deleted Photos": { [deletedKey(st.at)]: deletedEntry(path, type, why, st, st.at) } };
}

async function savePhotoFor(target: string, type: string, shot: { blob: Blob; contentType: string }): Promise<string> {
  const c = ctx!;
  const p = parseTarget(target);
  const nameId = p.breaker ? `${p.assetId}-${p.breaker}` : p.assetId;
  const taken = new Set<string>();
  try {
    for (const ph of await deviceList.photos()) taken.add(ph.path);
  } catch {
    // Device list unreadable: the name may repeat; the cloud refuses a second file with the same name.
  }
  const ext = shot.contentType === "image/png" ? "png" : "jpg";
  const path = photoPath(c.company, c.pack.siteNumber, nameId, today(), type, taken, ext);
  const old = recAt(target).photos[type];
  const extra = old && old !== path ? await retire(target, type, old, "Retaken") : {};
  const recordPath = await saveIea(target, { ...extra, Photos: { [type]: path } }, `${cardWord(target)} photo`);
  await savePhoto({ path, company: c.company, siteNumber: c.pack.siteNumber, assetId: nameId, recordPath, contentType: shot.contentType, blob: shot.blob, takenAt: new Date().toISOString() });
  c.paint();
  return path;
}

/** In memory only: the photo shows gone while Undo is still possible. */
function setPhotoInMemory(target: string, type: string, path: string): void {
  const p = parseTarget(target);
  s!.data[p.assetId] = mergeData(s!.data[p.assetId] ?? {}, placeChange({ assetId: p.assetId, breaker: p.breaker, data: { Photos: { [type]: path } } }));
  s!.recs[p.assetId] = recFromData(s!.data[p.assetId]);
}

/** What the photo store needs from Collect. */
export const photoHooks = {
  items: (target: string): ViewItem[] => {
    const r = recAt(target);
    if (isRowTarget(target)) {
      const pt = parseTarget(target);
      const point = pt.breaker ?? pt.assetId.slice(housingOf(pt.assetId).length + 1);
      return rowPhotoKeys(r).map((k) => ({ path: r.photos[k]!, type: k, target, label: k === "Row" ? point : `${point} · ${k}` }));
    }
    const order = [...labelChoices(cardFor(site(), parseTarget(target).assetId)), ...Object.keys(r.photos)];
    return [...new Set(order)].filter((t) => r.photos[t]).map((t) => ({ path: r.photos[t]!, type: t, target, label: t }));
  },
  labels: (target: string): string[] => {
    if (isRowTarget(target)) return [];
    const r = recAt(target);
    return [...new Set([...labelChoices(cardFor(site(), parseTarget(target).assetId)), ...Object.keys(r.photos)])];
  },
  title: (target: string): string => photoTitle(target),
  retake: (target: string, type: string): void => shootOne(target, type),
  hide: (target: string, type: string): void => {
    setPhotoInMemory(target, type, "");
    ctx?.paint();
  },
  restore: (target: string, type: string, path: string): void => {
    setPhotoInMemory(target, type, path);
    ctx?.paint();
  },
  commitDelete: async (target: string, type: string, path: string): Promise<void> => {
    const extra = await retire(target, type, path, "Deleted");
    await saveIea(target, { ...extra, Photos: { [type]: "" } }, `${cardWord(target)} photo deleted`);
  },
  relabel: async (target: string, from: string, to: string): Promise<void> => {
    const change = relabelChange(recAt(target), from, to);
    if (change) await saveIea(target, change, `${cardWord(target)} photo label`);
    ctx?.paint();
  },
};

export function shootOne(target: string, type: string): void {
  const cam = openCamera({
    title: photoTitle(target),
    label: type === "Row" ? photoTitle(target) : isRowTarget(target) ? `${photoTitle(target)} · ${type}` : type,
    run: false,
    onShot: (shot) => {
      cam.close();
      void savePhotoFor(target, type, shot);
      ctx!.flash(type === "Row" ? "Photo saved." : type.startsWith("Photo ") ? `${type} saved.` : `${type} photo saved.`);
    },
    onSkip: () => undefined,
    onDone: () => ctx!.paint(),
  });
}

/** One run of the camera through a list of shots. Each shot saves at once. Back redoes the last. */
function runCamera(title: string, shots: { target: string; type: string; words: string }[], doneWords: string): void {
  if (!shots.length) return;
  let i = 0;
  const saved: (string | null)[] = shots.map(() => null);
  const words = () => `${shots[i]!.words} · ${i + 1} of ${shots.length}`;
  const go = (n: number) => {
    i = n;
    if (i >= shots.length) {
      cam.close();
      ctx!.flash(doneWords);
      ctx!.paint();
      return;
    }
    cam.label(words());
    cam.canBack(i > 0);
  };
  const cam = openCamera({
    title,
    label: words(),
    run: true,
    onShot: (shot) => {
      const k = i;
      void savePhotoFor(shots[k]!.target, shots[k]!.type, shot).then((path) => {
        saved[k] = path;
      });
      go(i + 1);
    },
    onSkip: () => go(i + 1),
    onBack: () => go(Math.max(0, i - 1)),
    onRetakeLast: () => go(Math.max(0, i - 1)),
    onDeleteLast: () => {
      const k = Math.max(0, i - 1);
      const path = saved[k];
      if (path) void photoHooks.commitDelete(shots[k]!.target, shots[k]!.type, path).then(() => ctx!.paint());
      saved[k] = null;
      go(k);
      ctx!.flash("Photo deleted. Shoot it again or tap Skip.");
    },
    onDone: () => ctx!.paint(),
  });
}

/** Take Photos: the card's photos still needed, in field order, with a big label for each. */
export function takePhotos(target: string): void {
  const p = parseTarget(target);
  const types = guidedTypes(cardFor(site(), p.assetId), recAt(target));
  if (!types.length) {
    ctx!.flash("All photos taken. Tap a photo to check or retake it.");
    return;
  }
  runCamera(photoTitle(target), types.map((t) => ({ target, type: t, words: t })), "Photos done.");
}

/** Photo Run down a housing's rows. Each shot goes to the next row. Not Opened rows are skipped. */
export function photoRun(assetId: string): void {
  const st = site();
  let rows = rowsOf(st, assetId).filter((r) => rowRec(st, assetId, r).opened !== "Not Opened");
  // Gear is walked the way it stands: a lineup section by section, top to bottom;
  // a panel down the left side, then the right.
  const kind = gearKindFor(st, assetId);
  if (kind) {
    const pts = rows.map((r) => r.point);
    const order = kind === "panel" ? [...(pts.includes(MAIN_NAME) ? [MAIN_NAME] : []), ...panelWalk(panelSlots(pts).slots)] : walkOrder(pts);
    rows = [...rows].sort((a, b) => order.indexOf(a.point) - order.indexOf(b.point));
  }
  const tgt = (r: { point: string; assetId: string | null }) => (r.assetId ? `A:${r.assetId}` : `B:${assetId}:${r.point}`);
  runCamera(
    `Photo Run · ${assetId.replace(/^\d{2}-/, "")}`,
    rows.map((r) => ({ target: tgt(r), type: "Row", words: `${r.point}${rowRec(site(), assetId, r).photos.Row ? " (replaces its photo)" : ""}` })),
    "Photo Run done.",
  );
}

/** Same as Above: the row above's device fields, never the photo. */
export async function sameAbove(assetId: string, index: number): Promise<void> {
  const st = site();
  const rows = rowsOf(st, assetId);
  const up = rows[index - 1];
  const row = rows[index];
  if (!up || !row) return;
  const src = rowRec(st, assetId, up);
  const was = rowRec(st, assetId, row);
  const before = { f: { ...was.f }, src: { ...was.src }, opened: was.opened };
  const change: Record<string, unknown> = { Opened: src.opened || "Opened" };
  const sources: Record<string, string> = {};
  for (const k of ["Device Type", "Rating (A)", "Setting", "Starter Type", "Trip Type", "Frame Amps", "Manufacturer"]) {
    if (src.f[k] !== undefined) {
      change[k] = src.f[k];
      sources[k] = src.src[k] ?? "Typed";
    }
  }
  change["Field Sources"] = sources;
  s!.row = row.point;
  const target = row.assetId ? `A:${row.assetId}` : `B:${assetId}:${row.point}`;
  await saveIea(target, change, "Bucket or breaker");
  // 6-second Undo: the copied fields back as they were (Robert, 2026-10-28 10:22 AM).
  ctx!.undo(`${row.point} copied from ${up.point}. Photo not copied.`, () => {
    void saveIea(target, sameAboveUndo(before, Object.keys(sources)), "Bucket or breaker (Undo)").then(() => ctx?.paint());
  });
}

// ---- Wire ----

async function saveWire(linkId: string, w: Wire): Promise<void> {
  const c = ctx!;
  s!.wires[linkId] = w;
  s!.wireOrder = [...s!.wireOrder.filter((x) => x !== linkId), linkId];
  const l = c.links().find((x) => x.id === linkId);
  const st = c.stamp();
  const data = { ...wireRow(w), "Connection ID": linkId, "Site Number": st.siteNumber, "Recorded By": st.badgeId, "Holder Name": st.holderName, "Recorded On": st.deviceName, "Recorded At": st.at };
  await c.writeLink(linkId, data, l?.fromAsset ?? "", "Wire");
}

function wireNow(): { link: string; w: Wire } | null {
  if (!s || s.sheet?.kind !== "wire") return null;
  return { link: s.sheet.link, w: { ...wireOf(site(), s.sheet.link) } };
}

export async function setWire(field: string, value: string): Promise<void> {
  const cur = wireNow();
  if (!cur) return;
  const w = cur.w;
  if (!w.type) w.type = "Cable";
  if (field === "length" || field === "amps") {
    const n = Number(value);
    w[field] = value.trim() === "" || Number.isNaN(n) ? "" : n;
  } else if (field === "size" || field === "sets" || field === "material" || field === "insulation" || field === "raceway") w[field] = value;
  ctx!.paint();
  await saveWire(cur.link, w);
  ctx!.paint();
}

export async function setWireType(t: "Cable" | "Busway"): Promise<void> {
  const cur = wireNow();
  if (!cur) return;
  await saveWire(cur.link, { ...cur.w, type: t });
  ctx!.paint();
}

export async function quickPick(index: number): Promise<void> {
  const cur = wireNow();
  if (!cur) return;
  const picks = quickPicks(s!.wireOrder.map((id) => s!.wires[id]!).filter(Boolean));
  const p = picks[index];
  if (!p) return;
  await saveWire(cur.link, { ...p, length: cur.w.length });
  ctx!.flash("Filled from Quick Pick. Type the length.");
  ctx!.paint();
}

export async function sameAsLastWire(): Promise<void> {
  const cur = wireNow();
  if (!cur) return;
  const last = [...s!.wireOrder].reverse().find((id) => id !== cur.link && wireStatus(s!.wires[id] ?? emptyWire()) === "done");
  if (!last) {
    ctx!.flash("No wire saved yet.");
    return;
  }
  await saveWire(cur.link, { ...s!.wires[last]!, length: cur.w.length });
  ctx!.flash("Copied from the last wire. Type the length.");
  ctx!.paint();
}

// ---- Panel taps ----

export function closeSheet(): void {
  if (s) s.sheet = null;
  ctx?.paint();
}

export function hasSheet(): boolean {
  return Boolean(s?.sheet);
}

export function setTab(t: "device" | "wire"): void {
  if (s) s.tab = t;
  ctx?.paint();
}

export function openRow(point: string | null): void {
  if (!s) return;
  s.row = point;
  // A position tapped in the door's Table tab shows its bucket on the front, card open.
  if (s.gear && point) s.gear.view = "front";
  ctx?.paint();
  if (point) requestAnimationFrame(() => document.getElementById("cl-rowcard")?.scrollIntoView({ block: "start" }));
}

/** The bucket photo row drops down its tiles, and folds them back (Robert 2026-10-20). */
export function togglePhotos(target: string): void {
  if (!s) return;
  const k = `ph:${target}`;
  if (s.openCards.has(k)) s.openCards.delete(k);
  else s.openCards.add(k);
  ctx?.paint();
}

export function openCard(assetId: string): void {
  s?.openCards.add(assetId);
  ctx?.paint();
}

export function jump(key: string): void {
  if (!s) return;
  s.sheet = null;
  s.row = null;
  gearFor(key);
  const n = ctx!.tree().nodes.get(key);
  if (s.gear && n?.kind === "point") {
    s.row = n.ref;
    ctx!.select(`a:${n.asset}`);
    return;
  }
  ctx!.select(key);
}

